import React, { useCallback, useContext, useReducer, useState } from "react"
import { User } from "../../../../../mp-database"
import { Program } from "../../../../../../types/gqlTypes/graphql"
import { getPlatformSettings } from "../../../../../../services/ready-services/platformConfiguration/platformSettings/platformSettingsQueryResolvers"
import { closeSnackbar, enqueueSnackbar } from "notistack"
import { savePlatformSettings } from "../../../../../../services/ready-services/platformConfiguration/platformSettings/platformSettingsMutationResolvers"
import { ProgramsContext } from "../ProgramsContext"
import {
  defaultProgramReducer,
  getOrgRefScope,
  initialDefaultProgramLoading,
  ROLES_WITH_DEFAULT_PROGRAM
} from "./helpers"
import { PlatformSettingsContextType } from "./types"

export const PlatformSettingsContext =
  React.createContext<PlatformSettingsContextType | null>(null)

interface Props {
  user: User
}

export const PlatformSettingsProvider: React.FC<Props> = ({ children }) => {
  const { currentProgramSetter } = useContext(ProgramsContext)

  const [defaultProgram, setDefaultProgram] = useState<string>(null)

  const [defaultProgramLoading, defaultProgramLoadingDispatch] = useReducer(
    defaultProgramReducer,
    initialDefaultProgramLoading
  )

  const saveDefaultProgram = useCallback(
    async (user: User, selectedProgram: string, prevProgram: string) => {
      if (!user) return

      setDefaultProgram(selectedProgram)
      defaultProgramLoadingDispatch("startSaving")

      try {
        const savingResult = await savePlatformSettings({
          orgRefScope: getOrgRefScope(user),
          data: {
            defaultProgram: (selectedProgram as Program) || null
          }
        })

        if (!savingResult) throw Error("Saving Error")

        const succesSnackbarKey = `${selectedProgram}-setter-succes`

        enqueueSnackbar("Default Program updated.", {
          key: succesSnackbarKey,
          variant: "success",
          autoHideDuration: 2000,
          action: {
            UNDO: () => {
              saveDefaultProgram(user, prevProgram, selectedProgram)
              closeSnackbar(succesSnackbarKey)
            }
          }
        })
      } catch (error) {
        console.warn(error)

        setDefaultProgram(prevProgram)

        enqueueSnackbar("Error occured while saving default program.", {
          variant: "error"
        })
      } finally {
        defaultProgramLoadingDispatch("stopSaving")
      }
    },
    []
  )

  const getDefaultProgram = useCallback(
    async (user: User) => {
      if (!user || !ROLES_WITH_DEFAULT_PROGRAM.includes(user.role)) {
        return
      }

      defaultProgramLoadingDispatch("startFetching")

      try {
        const { defaultProgram } = await getPlatformSettings({
          orgRefScope: getOrgRefScope(user)
        })

        if (!defaultProgram) return null

        return defaultProgram
      } catch (error) {
        console.warn(error)

        enqueueSnackbar("Error occured while getting default program.", {
          variant: "error"
        })

        return null
      } finally {
        defaultProgramLoadingDispatch("stopFetching")
      }
    },
    [currentProgramSetter]
  )

  const defaultProgramSetter = useCallback(
    async (user: User, isCurrentProgram = false) => {
      const defaultProgram = await getDefaultProgram(user)

      if (!defaultProgram) return

      if (!!isCurrentProgram) {
        currentProgramSetter(defaultProgram.toLowerCase())
      }

      setDefaultProgram(defaultProgram)
    },
    [currentProgramSetter, getDefaultProgram]
  )

  return (
    <PlatformSettingsContext.Provider
      value={{
        defaultProgram,
        defaultProgramLoading,
        saveDefaultProgram,
        defaultProgramSetter
      }}
    >
      {children}
    </PlatformSettingsContext.Provider>
  )
}
